import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { BatchHttpLink as HttpLink } from 'apollo-link-batch-http';
import { onError } from 'apollo-link-error';
import logger from 'apollo-link-logger';

import { getGraphQLUrl } from './features/configuration/selectors';
import { getCredentials } from './features/authentication/selectors';
import { logoutUser } from './features/authentication/actions';
import { isApolloNetworkError, isFetchNetworkError } from './utils/errorTypeCheck';


const configureLink = (store) => {
  const graphQLUrl = getGraphQLUrl(store.getState());

  const httpLink = new HttpLink({
    uri: graphQLUrl,
  });

  /*
  * authenticationLink: Fetch credentials off of the redux store and inject them as HTTP headers
  *
  * The HTTP link above adds a `headers` field to the client context.
  * Depends on credentials default value.
   */
  const authenticationLink = setContext((request, previousContext) => {
    const credentials = getCredentials(store.getState());
    const responseHeaders = localStorage.getItem('credentials');

    const headerExpiry = (credentials) ? credentials.expiry : 0;
    const responseExpiry = (responseHeaders) ? parseInt(JSON.parse(responseHeaders).expiry, 10) : 0;

    let headers;
    if (responseHeaders && (responseExpiry > headerExpiry)) {
      headers = {
        ...previousContext.headers,
        ...JSON.parse(responseHeaders),
      };
    } else {
      headers = {
        ...previousContext.headers,
        ...credentials,
      };
    }

    return { headers };
  });

  /*
  * unauthenticatedErrorLink: Monitor client errors for HTTP 401 errors and dispatch logout action
  * when required.
   */
  const unauthenticatedErrorLink = onError((error) => {
    if (!isApolloNetworkError(error)) {
      return;
    }
    if (error.networkError.statusCode === 401) {
      store.dispatch(logoutUser());
    }
  });

  /*
  * noConnectionErrorLink: Monitor client HTTP fetch errors and dispatch informative message to
  * user about the network interruption.
   */
  const noConnectionErrorLink = onError((error) => {
    if (error.graphQLErrors) {
      error.graphQLErrors.forEach(({ message, locations, path }) => {
        // eslint-disable-next-line no-console
        console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      });
    }
    if (!isApolloNetworkError(error)) {
      return;
    }
    if (!isFetchNetworkError(error.networkError)) {
      return;
    }

    /* eslint-disable no-console */
    console.warn('Could not reach server.');
    /* eslint-enable no-console */
  });

  /*
  * headersLink: building out the HTTP headers response
   */
  const headerResponseLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext();
      const { response: { headers } } = context;

      if (headers) {
        const credentialsProperties = ['access-token', 'uid', 'expiry', 'client', 'token-type'];
        const newCreds = {};
        credentialsProperties.forEach((field) => {
          newCreds[field] = headers.get(field);
        });

        if (newCreds.expiry && newCreds['access-token']) {
          localStorage.setItem('credentials', JSON.stringify(newCreds));
        }
      }
      return response;
    });
  });

  /*
  * Link concatenation: Order matters!
   */
  const links = [
    headerResponseLink,
    authenticationLink,
    noConnectionErrorLink,
    unauthenticatedErrorLink,
    httpLink,
  ];

  if (process.env.NODE_ENV !== 'production') {
    links.unshift(logger);
  }

  const link = ApolloLink.from(links);

  return link;
};


export default configureLink;
